import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { LEGAL_DOC_ORDER, type LegalDocId } from '@redevicer/shared'
export interface ProfileDropdownProps {
  isOpen: boolean
  onClose: () => void
  displayName: string
  email?: string
  isAdmin: boolean
  onSeeProfile: () => void
  onMyListings: () => void
  onMyOffers: () => void
  onMyChats: () => void
  onMyReports: () => void
  onExamples: () => void
  onAdmin?: () => void
  onLogout: () => void
  onOpenLegal: (docId: LegalDocId) => void
  /** Browse-without-login: show sign-in CTA only */
  guest?: boolean
  onLogin?: () => void
}

export default function ProfileDropdown({
  isOpen,
  onClose,
  displayName,
  email,
  isAdmin,
  onSeeProfile,
  onMyListings,
  onMyOffers,
  onMyChats,
  onMyReports,
  onExamples,
  onAdmin,
  onLogout,
  onOpenLegal,
  guest,
  onLogin,
}: ProfileDropdownProps) {
  const { t } = useTranslation()
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen) return
    function onPointerDown(e: MouseEvent | TouchEvent) {
      const el = panelRef.current
      if (!el || !(e.target instanceof Node)) return
      if (!el.contains(e.target)) onClose()
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('touchstart', onPointerDown, { passive: true })
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('touchstart', onPointerDown)
    }
  }, [isOpen, onClose])

  useEffect(() => {
    if (!isOpen) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [isOpen, onClose])

  if (!isOpen) return null

  if (guest) {
    return (
      <div className="vc-profile-dropdown" ref={panelRef} role="dialog" aria-label={t('vc.profile.menuAria')}>
        <div className="vc-profile-head">
          <p className="vc-profile-name">{t('vc.guest.dropdownTitle')}</p>
          <p className="vc-profile-email">{t('vc.guest.dropdownHint')}</p>
          <button
            type="button"
            className="vc-profile-login-btn"
            onClick={() => {
              onLogin?.()
              onClose()
            }}
          >
            {t('auth.login')} / {t('auth.register')}
          </button>
        </div>
        <div className="vc-profile-section">
          <p className="vc-profile-section-title">{t('vc.profile.legal')}</p>
          {LEGAL_DOC_ORDER.map((docId) => (
            <button
              key={docId}
              type="button"
              className="vc-profile-row"
              onClick={() => {
                onOpenLegal(docId)
                onClose()
              }}
            >
              <span className="vc-profile-row-label">{t(`legal.doc.${docId}`)}</span>
            </button>
          ))}
        </div>
      </div>
    )
  }

  function row(onClick: () => void, label: string, hint?: string) {
    return (
      <button type="button" className="vc-profile-row" onClick={() => { onClick(); onClose(); }}>
        <span className="vc-profile-row-label">{label}</span>
        {hint && <span className="vc-profile-row-hint">{hint}</span>}
      </button>
    )
  }

  return (
    <div className="vc-profile-dropdown" ref={panelRef} role="dialog" aria-label={t('vc.profile.menuAria')}>
      <div className="vc-profile-head">
        <p className="vc-profile-name">{displayName}</p>
        {email && <p className="vc-profile-email">{email}</p>}
        <button type="button" className="vc-profile-text-btn" onClick={() => { onSeeProfile(); onClose(); }}>
          {t('vc.profile.seeProfile')}
        </button>
      </div>

      <div className="vc-profile-section">
        <p className="vc-profile-section-title">{t('vc.profile.buying')}</p>
        {row(onMyOffers, t('vc.profile.ordersOffers'))}
        {row(onMyChats, t('offerChats.title'))}
        {row(onMyReports, t('account.tabReports'))}
        <button type="button" className="vc-profile-row vc-profile-row--muted" disabled>
          <span className="vc-profile-row-label">{t('vc.profile.savedSearches')}</span>
          <span className="vc-profile-row-hint">{t('vc.profile.comingSoon')}</span>
        </button>
      </div>

      <div className="vc-profile-section">
        <p className="vc-profile-section-title">{t('vc.profile.selling')}</p>
        {row(onMyListings, t('menu.myListings'))}
      </div>

      <div className="vc-profile-section">
        <p className="vc-profile-section-title">{t('vc.profile.help')}</p>
        {row(onExamples, t('menu.examples'))}
      </div>

      <div className="vc-profile-section">
        <p className="vc-profile-section-title">{t('vc.profile.legal')}</p>
        {LEGAL_DOC_ORDER.map((docId) => (
          <button
            key={docId}
            type="button"
            className="vc-profile-row"
            onClick={() => {
              onOpenLegal(docId)
              onClose()
            }}
          >
            <span className="vc-profile-row-label">{t(`legal.doc.${docId}`)}</span>
          </button>
        ))}
      </div>

      {isAdmin && onAdmin && (
        <div className="vc-profile-section">
          <p className="vc-profile-section-title">{t('vc.profile.internal')}</p>
          {row(onAdmin, t('menu.admin'))}
        </div>
      )}

      <div className="vc-profile-footer">
        <button type="button" className="vc-profile-logout" onClick={() => { onLogout(); onClose(); }}>
          {t('menu.logout')}
        </button>
      </div>
    </div>
  )
}
